import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, inject, signal } from '@angular/core';
import { Observable, catchError, map, tap, throwError } from 'rxjs';
import { APP_SCHEMA_ENDPOINTS } from '../api';

/**
 * Student Document Model
 */
export interface StudentDocument {
  name: string;
  size: number;
  last_modified: string | null;
  content_type: string | null;
  category?: string;
}

/**
 * Document Upload Response
 */
export interface DocumentUploadResponse {
  success: boolean;
  contact_id: number;
  filename: string;
  category?: string;
  size_bytes: number;
  content_type: string;
  overwritten: boolean;
}

/**
 * Documents List Response
 */
export interface DocumentsListResponse {
  contact_id: number;
  documents: StudentDocument[];
  count: number;
}

/**
 * Student Document Service
 *
 * Handles student document operations with Azure Blob Storage.
 * Documents are stored in student_documents/{category}/{contact_id}/{filename}
 */
@Injectable({
  providedIn: 'root',
})
export class StudentDocumentService {
  private http = inject(HttpClient);

  // Loading and error states
  readonly loading = signal<boolean>(false);
  readonly error = signal<string | null>(null);

  // Cache for documents list
  private documentsCache = signal<Map<number, StudentDocument[]>>(new Map());

  /**
   * Upload a document for a student
   */
  uploadDocument(
    contactId: number,
    file: File,
    overwrite: boolean = false,
    customFilename?: string,
    category: string = 'admission'
  ): Observable<DocumentUploadResponse> {
    this.loading.set(true);
    this.error.set(null);

    const formData = new FormData();

    // If a custom filename is provided, create a new File object with the custom name
    if (customFilename) {
      const fileExtension = this.getFileExtension(file.name);
      const extWithDot = fileExtension ? `.${fileExtension}` : '';

      // Check if custom filename already ends with the extension
      const newFilename = customFilename.toLowerCase().endsWith(extWithDot.toLowerCase())
        ? customFilename
        : `${customFilename}${extWithDot}`;

      const renamedFile = new File([file], newFilename, { type: file.type });
      formData.append('file', renamedFile);
    } else {
      formData.append('file', file);
    }

    const encodedCategory = encodeURIComponent(category);
    const url = `${APP_SCHEMA_ENDPOINTS.contacts.list}${contactId}/documents?overwrite=${overwrite}&category=${encodedCategory}`;

    return this.http.post<DocumentUploadResponse>(url, formData).pipe(
      tap(() => {
        this.loading.set(false);
        // Clear cache for this contact
        this.clearContactCache(contactId);
      }),
      catchError((error) => {
        this.loading.set(false);
        const errorMessage = error.error?.detail || 'Fehler beim Hochladen des Dokuments';
        this.error.set(errorMessage);
        console.error('❌ Error uploading document:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * List all documents for a student
   */
  listDocuments(contactId: number, useCache: boolean = true): Observable<StudentDocument[]> {
    // Return cached documents if available
    const cached = this.documentsCache().get(contactId);
    if (cached && useCache) {
      return new Observable((observer) => {
        observer.next(cached);
        observer.complete();
      });
    }

    this.loading.set(true);
    this.error.set(null);

    const url = `${APP_SCHEMA_ENDPOINTS.contacts.list}${contactId}/documents`;

    return this.http.get<DocumentsListResponse>(url).pipe(
      map((response) => response.documents),
      tap((documents) => {
        this.loading.set(false);
        // Update cache
        this.documentsCache.update((cache) => {
          const newCache = new Map(cache);
          newCache.set(contactId, documents);
          return newCache;
        });
      }),
      catchError((error) => {
        this.loading.set(false);
        const errorMessage = error.error?.detail || 'Fehler beim Laden der Dokumente';
        this.error.set(errorMessage);
        console.error('❌ Error listing documents:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Download a document
   * Returns the document URL for download
   */
  getDocumentDownloadUrl(contactId: number, filename: string): string {
    return `${APP_SCHEMA_ENDPOINTS.contacts.list}${contactId}/documents/${encodeURIComponent(
      filename
    )}`;
  }

  /**
   * Download a document as blob
   */
  downloadDocument(contactId: number, filename: string): Observable<Blob> {
    this.loading.set(true);
    this.error.set(null);

    const url = this.getDocumentDownloadUrl(contactId, filename);

    return this.http.get(url, { responseType: 'blob' }).pipe(
      tap(() => {
        this.loading.set(false);
      }),
      catchError((error) => {
        this.loading.set(false);
        const errorMessage = error.error?.detail || 'Fehler beim Herunterladen des Dokuments';
        this.error.set(errorMessage);
        console.error('❌ Error downloading document:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Delete a document
   */
  deleteDocument(contactId: number, filename: string): Observable<{ success: boolean }> {
    this.loading.set(true);
    this.error.set(null);

    const url = `${APP_SCHEMA_ENDPOINTS.contacts.list}${contactId}/documents/${encodeURIComponent(
      filename
    )}`;

    return this.http.delete<{ success: boolean }>(url).pipe(
      tap(() => {
        this.loading.set(false);
        // Clear cache for this contact
        this.clearContactCache(contactId);
      }),
      catchError((error) => {
        this.loading.set(false);
        const errorMessage = error.error?.detail || 'Fehler beim Löschen des Dokuments';
        this.error.set(errorMessage);
        console.error('❌ Error deleting document:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Clear cache for a specific contact
   */
  private clearContactCache(contactId: number): void {
    this.documentsCache.update((cache) => {
      const newCache = new Map(cache);
      newCache.delete(contactId);
      return newCache;
    });
  }

  /**
   * Clear all caches
   */
  clearCache(): void {
    this.documentsCache.set(new Map());
  }

  /**
   * Format file size to human-readable format
   */
  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';

    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  }

  /**
   * Get file extension from filename
   */
  getFileExtension(filename: string): string {
    return filename.split('.').pop()?.toLowerCase() || '';
  }

  /**
   * Get Material Symbols icon name for file type
   */
  getFileIcon(filename: string): string {
    const ext = this.getFileExtension(filename);

    const iconMap: Record<string, string> = {
      pdf: 'picture_as_pdf',
      doc: 'description',
      docx: 'description',
      xls: 'table_chart',
      xlsx: 'table_chart',
      ppt: 'slideshow',
      pptx: 'slideshow',
      txt: 'article',
      jpg: 'image',
      jpeg: 'image',
      png: 'image',
      gif: 'image',
      zip: 'folder_zip',
      rar: 'folder_zip',
      '7z': 'folder_zip',
    };

    return iconMap[ext] || 'draft';
  }
}
